import {
	// eslint-disable-next-line no-restricted-imports
	createContext,
	PropsWithChildren,
	// eslint-disable-next-line no-restricted-imports
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { Layout } from 'react-grid-layout';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, UseQueryResult } from 'react-query';
// eslint-disable-next-line no-restricted-imports
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { Modal } from 'antd';
import getDashboard from 'api/v1/dashboards/id/get';
import locked from 'api/v1/dashboards/id/lock';
import { ALL_SELECTED_VALUE } from 'components/NewSelect/utils';
import { REACT_QUERY_KEY } from 'constants/reactQueryKeys';
import ROUTES from 'constants/routes';
import dayjs, { Dayjs } from 'dayjs';
import { useDashboardVariablesFromLocalStorage } from 'hooks/dashboard/useDashboardFromLocalStorage';
import useVariablesFromUrl from 'hooks/dashboard/useVariablesFromUrl';
import useTabVisibility from 'hooks/useTabFocus';
import { getUpdatedLayout } from 'lib/dashboard/getUpdatedLayout';
import { getMinMaxForSelectedTime } from 'lib/getMinMax';
import { defaultTo, isEmpty } from 'lodash-es';
import isEqual from 'lodash-es/isEqual';
import isUndefined from 'lodash-es/isUndefined';
import omitBy from 'lodash-es/omitBy';
import { useAppContext } from 'providers/App/App';
import { initializeDefaultVariables } from 'providers/Dashboard/initializeDefaultVariables';
import { normalizeUrlValueForVariable } from 'providers/Dashboard/normalizeUrlValue';
import { useErrorModal } from 'providers/ErrorModalProvider';
// eslint-disable-next-line no-restricted-imports
import { Dispatch } from 'redux';
import { AppState } from 'store/reducers';
import AppActions from 'types/actions';
import { UPDATE_TIME_INTERVAL } from 'types/actions/globalTime';
import { SuccessResponseV2 } from 'types/api';
import { Dashboard, IDashboardVariable } from 'types/api/dashboard/getAll';
import APIError from 'types/api/error';
import { GlobalReducer } from 'types/reducer/globalTime';
import { v4 as generateUUID } from 'uuid';

import { useDashboardVariablesSelector } from '../../hooks/dashboard/useDashboardVariables';
import {
	setDashboardVariablesStore,
	updateDashboardVariablesStore,
} from './store/dashboardVariables/dashboardVariablesStore';
import { IDashboardContext, WidgetColumnWidths } from './types';
import { sortLayout } from './util';

export const DashboardContext = createContext<IDashboardContext>({
	isDashboardSliderOpen: false,
	isDashboardLocked: false,
	handleToggleDashboardSlider: () => {},
	handleDashboardLockToggle: () => {},
	dashboardResponse: {} as UseQueryResult<
		SuccessResponseV2<Dashboard>,
		APIError
	>,
	selectedDashboard: {} as Dashboard,
	dashboardId: '',
	layouts: [],
	panelMap: {},
	setPanelMap: () => {},

	setLayouts: () => {},
	setSelectedDashboard: () => {},
	updatedTimeRef: {} as React.MutableRefObject<Dayjs | null>,
	toScrollWidgetId: '',
	setToScrollWidgetId: () => {},
	updateLocalStorageDashboardVariables: () => {},
	dashboardQueryRangeCalled: false,
	setDashboardQueryRangeCalled: () => {},
	selectedRowWidgetId: '',
	setSelectedRowWidgetId: () => {},
	isDashboardFetching: false,
	columnWidths: {},
	setColumnWidths: () => {},
});

interface Props {
	dashboardId: string;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function DashboardProvider({
	children,
}: PropsWithChildren): JSX.Element {
	const [isDashboardSliderOpen, setIsDashboardSlider] = useState<boolean>(false);

	const [toScrollWidgetId, setToScrollWidgetId] = useState<string>('');

	const [isDashboardLocked, setIsDashboardLocked] = useState<boolean>(false);

	const [selectedRowWidgetId, setSelectedRowWidgetId] = useState<string | null>(
		null,
	);

	const [
		dashboardQueryRangeCalled,
		setDashboardQueryRangeCalled,
	] = useState<boolean>(false);

	const isDashboardPage = useRouteMatch<Props>({
		path: ROUTES.DASHBOARD,
		exact: true,
	});

	const { showErrorModal } = useErrorModal();

	const dispatch = useDispatch<Dispatch<AppActions>>();

	const globalTime = useSelector<AppState, GlobalReducer>(
		(state) => state.globalTime,
	);

	const [onModal, Content] = Modal.useModal();

	const isDashboardWidgetPage = useRouteMatch<Props>({
		path: ROUTES.DASHBOARD_WIDGET,
		exact: true,
	});

	const [layouts, setLayouts] = useState<Layout[]>([]);

	const [panelMap, setPanelMap] = useState<
		Record<string, { widgets: Layout[]; collapsed: boolean }>
	>({});

	const { isLoggedIn } = useAppContext();

	const dashboardId =
		(isDashboardPage
			? isDashboardPage.params.dashboardId
			: isDashboardWidgetPage?.params.dashboardId) || '';

	const [selectedDashboard, setSelectedDashboard] = useState<Dashboard>();
	const dashboardVariables = useDashboardVariablesSelector((s) => s.variables);
	const savedDashboardId = useDashboardVariablesSelector((s) => s.dashboardId);

	useEffect(() => {
		const existingVariables = dashboardVariables;
		const updatedVariables = selectedDashboard?.data.variables || {};

		if (savedDashboardId !== dashboardId) {
			setDashboardVariablesStore({
				dashboardId,
				variables: updatedVariables,
			});
		} else if (!isEqual(existingVariables, updatedVariables)) {
			updateDashboardVariablesStore({
				dashboardId,
				variables: updatedVariables,
			});
		}
	}, [selectedDashboard]);

	const {
		currentDashboard,
		updateLocalStorageDashboardVariables,
	} = useDashboardVariablesFromLocalStorage(dashboardId);

	const { getUrlVariables, updateUrlVariable } = useVariablesFromUrl();

	const updatedTimeRef = useRef<Dayjs | null>(null); // Using ref to store the updated time
	const modalRef = useRef<any>(null);

	const isVisible = useTabVisibility();

	const { t } = useTranslation(['dashboard']);
	const dashboardRef = useRef<Dashboard>();

	const [isDashboardFetching, setIsDashboardFetching] = useState<boolean>(false);

	const mergeDBWithLocalStorage = (
		data: Dashboard,
		localStorageVariables: any,
	): Dashboard => {
		const updatedData = data;
		if (data && localStorageVariables) {
			const updatedVariables = data.data.variables;
			const variablesFromUrl = getUrlVariables();
			Object.keys(data.data.variables).forEach((variable) => {
				const variableData = data.data.variables[variable];

				// values from url
				const urlVariable = variableData?.name
					? variablesFromUrl[variableData?.name] || variablesFromUrl[variableData.id]
					: variablesFromUrl[variableData.id];

				let updatedVariable = {
					...data.data.variables[variable],
					...localStorageVariables[variableData.name as any],
				};

				// respect the url variable if it is set, override the others
				if (!isEmpty(urlVariable)) {
					if (urlVariable === ALL_SELECTED_VALUE) {
						updatedVariable = {
							...updatedVariable,
							allSelected: true,
						};
					} else {
						// Normalize URL value to match variable's multiSelect configuration
						const normalizedValue = normalizeUrlValueForVariable(
							urlVariable,
							variableData,
						);

						updatedVariable = {
							...updatedVariable,
							selectedValue: normalizedValue,
							// Only set allSelected to false if showALLOption is available
							...(updatedVariable?.showALLOption && { allSelected: false }),
						};
					}
				}

				updatedVariables[variable] = updatedVariable;
			});
			updatedData.data.variables = updatedVariables;
		}
		return updatedData;
	};
	// As we do not have order and ID's in the variables object, we have to process variables to add order and ID if they do not exist in the variables object
	// eslint-disable-next-line sonarjs/cognitive-complexity
	const transformDashboardVariables = (data: Dashboard): Dashboard => {
		if (data && data.data && data.data.variables) {
			const clonedDashboardData = mergeDBWithLocalStorage(
				JSON.parse(JSON.stringify(data)),
				currentDashboard,
			);
			const { variables } = clonedDashboardData.data;
			const existingOrders: Set<number> = new Set();

			for (const key in variables) {
				// eslint-disable-next-line no-prototype-builtins
				if (variables.hasOwnProperty(key)) {
					const variable: IDashboardVariable = variables[key];

					// Check if 'order' property doesn't exist or is undefined
					if (variable.order === undefined) {
						// Find a unique order starting from 0
						let order = 0;
						while (existingOrders.has(order)) {
							order += 1;
						}

						variable.order = order;
						existingOrders.add(order);
						// ! BWC - Specific case for backward compatibility where textboxValue was used instead of defaultValue
						if (variable.type === 'TEXTBOX' && !variable.defaultValue) {
							variable.defaultValue = variable.textboxValue || '';
						}
					}

					if (variable.id === undefined) {
						variable.id = generateUUID();
					}
				}
			}

			return clonedDashboardData;
		}

		return data;
	};
	const dashboardResponse = useQuery(
		[REACT_QUERY_KEY.DASHBOARD_BY_ID, isDashboardPage?.params, dashboardId],
		{
			enabled: (!!isDashboardPage || !!isDashboardWidgetPage) && isLoggedIn,
			queryFn: async () => {
				setIsDashboardFetching(true);
				try {
					return await getDashboard({
						id: dashboardId,
					});
				} catch (error) {
					showErrorModal(error as APIError);
					return;
				} finally {
					setIsDashboardFetching(false);
				}
			},
			refetchOnWindowFocus: false,
			onError: (error) => {
				showErrorModal(error as APIError);
			},

			onSuccess: (data: SuccessResponseV2<Dashboard>) => {
				// if the url variable is not set for any variable, set it to the default value
				const variables = data?.data?.data?.variables;
				if (variables) {
					initializeDefaultVariables(variables, getUrlVariables, updateUrlVariable);
				}

				const updatedDashboardData = transformDashboardVariables(data?.data);
				const updatedDate = dayjs(updatedDashboardData?.updatedAt);

				setIsDashboardLocked(updatedDashboardData?.locked || false);

				// on first render
				if (updatedTimeRef.current === null) {
					setSelectedDashboard(updatedDashboardData);

					updatedTimeRef.current = updatedDate;

					dashboardRef.current = updatedDashboardData;

					setLayouts(
						sortLayout(getUpdatedLayout(updatedDashboardData?.data.layout)),
					);

					setPanelMap(defaultTo(updatedDashboardData?.data?.panelMap, {}));
				}

				if (
					updatedTimeRef.current !== null &&
					updatedDate.isAfter(updatedTimeRef.current) &&
					isVisible &&
					dashboardRef.current?.id === updatedDashboardData?.id
				) {
					// show modal when state is out of sync
					const modal = onModal.confirm({
						centered: true,
						title: t('dashboard_has_been_updated'),
						content: t('do_you_want_to_refresh_the_dashboard'),
						onOk() {
							setSelectedDashboard(updatedDashboardData);

							const { maxTime, minTime } = getMinMaxForSelectedTime(
								globalTime.selectedTime,
								globalTime.minTime,
								globalTime.maxTime,
							);

							dispatch({
								type: UPDATE_TIME_INTERVAL,
								payload: {
									maxTime,
									minTime,
									selectedTime: globalTime.selectedTime,
								},
							});

							dashboardRef.current = updatedDashboardData;

							updatedTimeRef.current = dayjs(updatedDashboardData?.updatedAt);

							setLayouts(
								sortLayout(getUpdatedLayout(updatedDashboardData?.data.layout)),
							);

							setPanelMap(defaultTo(updatedDashboardData?.data.panelMap, {}));
						},
					});

					modalRef.current = modal;
				} else {
					// normal flow
					updatedTimeRef.current = dayjs(updatedDashboardData?.updatedAt);

					dashboardRef.current = updatedDashboardData;

					if (!isEqual(selectedDashboard, updatedDashboardData)) {
						setSelectedDashboard(updatedDashboardData);
					}

					if (
						!isEqual(
							[omitBy(layouts, (value): boolean => isUndefined(value))[0]],
							updatedDashboardData?.data.layout,
						)
					) {
						setLayouts(
							sortLayout(getUpdatedLayout(updatedDashboardData?.data.layout)),
						);

						setPanelMap(defaultTo(updatedDashboardData?.data.panelMap, {}));
					}
				}
			},
		},
	);

	useEffect(() => {
		// make the call on tab visibility only if the user is on dashboard / widget page
		if (
			isVisible &&
			updatedTimeRef.current &&
			(!!isDashboardPage || !!isDashboardWidgetPage)
		) {
			dashboardResponse.refetch();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isVisible]);

	useEffect(() => {
		if (!isVisible && modalRef.current) {
			modalRef.current.destroy();
		}
	}, [isVisible]);

	const handleToggleDashboardSlider = (value: boolean): void => {
		setIsDashboardSlider(value);
	};

	const { mutate: lockDashboard } = useMutation(locked, {
		onSuccess: (_, props) => {
			setIsDashboardSlider(false);
			setIsDashboardLocked(props.lock);
		},
		onError: (error) => {
			showErrorModal(error as APIError);
		},
	});

	const handleDashboardLockToggle = async (value: boolean): Promise<void> => {
		if (selectedDashboard) {
			try {
				await lockDashboard({
					id: selectedDashboard.id,
					lock: value,
				});
			} catch (error) {
				showErrorModal(error as APIError);
			}
		}
	};

	const [columnWidths, setColumnWidths] = useState<WidgetColumnWidths>({});

	const value: IDashboardContext = useMemo(
		() => ({
			toScrollWidgetId,
			isDashboardSliderOpen,
			isDashboardLocked,
			handleToggleDashboardSlider,
			handleDashboardLockToggle,
			dashboardResponse,
			selectedDashboard,
			dashboardId,
			layouts,
			panelMap,
			setLayouts,
			setPanelMap,
			setSelectedDashboard,
			updatedTimeRef,
			setToScrollWidgetId,
			updateLocalStorageDashboardVariables,
			dashboardQueryRangeCalled,
			setDashboardQueryRangeCalled,
			selectedRowWidgetId,
			setSelectedRowWidgetId,
			isDashboardFetching,
			columnWidths,
			setColumnWidths,
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			isDashboardSliderOpen,
			isDashboardLocked,
			dashboardResponse,
			selectedDashboard,
			dashboardId,
			layouts,
			panelMap,
			toScrollWidgetId,
			updateLocalStorageDashboardVariables,
			currentDashboard,
			dashboardQueryRangeCalled,
			setDashboardQueryRangeCalled,
			selectedRowWidgetId,
			setSelectedRowWidgetId,
			isDashboardFetching,
			columnWidths,
			setColumnWidths,
		],
	);

	return (
		<DashboardContext.Provider value={value}>
			{Content}
			{children}
		</DashboardContext.Provider>
	);
}

export const useDashboard = (): IDashboardContext => {
	const context = useContext(DashboardContext);

	if (!context) {
		throw new Error('Should be used inside the context');
	}

	return context;
};
